/**
 * ModelLoader - 模型加载编排器
 *
 * 封装 LOD / 单文件的加载流程，以及三个加载器的懒加载管理。
 */

import * as THREE from 'three'
import type { GSplatClipState } from '@/plugins/gsplat/clipping/GSplatClipState'
import { GaussianSogsMesh } from '@/plugins/gsplat/core/GaussianSogsMesh'
import { GaussianSplatMesh } from '@/plugins/gsplat/core/GaussianSplatMesh'
import { GSplatSogsData } from '@/plugins/gsplat/core/GSplatSogsData'
import { UnifiedLODCentersPoints } from '@/plugins/gsplat/core/UnifiedLODCentersPoints'
import { UnifiedLODMesh } from '@/plugins/gsplat/core/UnifiedLODMesh'
import { SOGBundleLoader } from '@/plugins/gsplat/loaders/SOGBundleLoader'
import { getSOGLodOffset, SOGLoader } from '@/plugins/gsplat/loaders/SOGLoader'
import { SogsParser } from '@/plugins/gsplat/loaders/SogsParser'
import { SplatLoader } from '@/plugins/gsplat/loaders/SplatLoader'
import { GSplatAssetLoader } from '@/plugins/gsplat/lod/GSplatAssetLoader'
import type { GSplatDirector } from '@/plugins/gsplat/lod/GSplatDirector'
import { GSplatOctree } from '@/plugins/gsplat/lod/GSplatOctree'
import { GSplatPlacement } from '@/plugins/gsplat/lod/GSplatPlacement'
import { Layer } from '@/plugins/gsplat/lod/Layer'
import type { LayerComposition } from '@/plugins/gsplat/lod/LayerComposition'
import {
  type CentersDisplayMode,
  DEFAULT_CENTERS_STYLE,
} from '@/plugins/gsplat/materials/CentersPointsMaterial'
import {
  measureGSplatLoadStage,
  measureGSplatLoadStageSync,
} from '@/plugins/gsplat/telemetry/GSplatLoadTelemetry'
import type { LODConfig } from '@/plugins/gsplat/types/ConfigTypes'
import { applyAssetRootTransform } from '@/plugins/gsplat/utils/applyAssetRootTransform'
import { getLoggerManager } from '@/shared/logging'
import type { BoundingBox, LoadOptions, LoadResult } from '@/shared/types/gsplat'
import type { ModelRegistry } from './ModelRegistry'
import type { LODGroupEntry, SingleFileEntry } from './types'

/** 高斯加载链路 logger（命名空间 gsplat），perf 埋点经 log.perf 输出 */
const log = getLoggerManager().getLogger('gsplat')

/** 在保留原始异常对象的前提下附加初始化阶段，供宿主诊断使用。 */
function annotateLodLoadError(error: unknown, stage: string): void {
  if (!(error instanceof Error)) {
    return
  }
  try {
    Object.defineProperty(error, 'gsplatLoadStage', {
      configurable: true,
      enumerable: false,
      value: stage,
      writable: true,
    })
  } catch {
    // 冻结的第三方异常不能附加诊断字段；不影响原始异常继续抛出。
  }
}

/** ModelLoader 依赖的上下文 */
export interface ModelLoaderContext {
  /** 挂载模型的场景 */
  scene: THREE.Scene
  /** 当前 WebGL 渲染器 */
  renderer: THREE.WebGLRenderer
  /** 模型注册表 */
  registry: ModelRegistry
  /** 当前 LOD Director，尚未就绪时为 null */
  director: GSplatDirector | null
  /** 当前 LayerComposition，尚未就绪时为 null */
  composition: LayerComposition | null
  /** 初始化 LOD 系统（由 PCManager 提供） */
  initLODSystem: () => { director: GSplatDirector; composition: LayerComposition }
  /** 获取 LOD 配置 */
  getLodConfig: () => LODConfig
  /** 同步最新 LOD 配置到运行时对象 */
  syncLodConfig?: () => void
  /** 获取当前 unified LOD polygon 裁切状态 */
  getClipState?: () => GSplatClipState
  /** 触发事件 */
  emit: (event: string, data: any) => void
  /** 登记 nodeId，在首个 LOD 实例出现后追踪其稳定性 */
  trackLodStability: (nodeId: string) => void
  /** 按 nodeId 移除已有模型 */
  removeByNodeId: (nodeId: string) => boolean
  /** 是否携带跨域 Cookie */
  withCredentials?: boolean
}

/** 模型加载编排器 */
export class ModelLoader {
  /** Splat 加载器（懒加载） */
  private splatLoader: SplatLoader | null = null

  /** SOG Bundle 加载器（懒加载） */
  private sogBundleLoader: SOGBundleLoader | null = null

  constructor(private ctx: ModelLoaderContext) {}

  // ==================== 公共方法 ====================

  /**
   * 加载 3DGS 模型。
   *
   * @param url - 资源 URL，支持 .sog / .ply / .splat / .json（LOD 元信息）
   * @param options - 可选加载参数
   * @returns 加载结果，含生成的 nodeId、numSplats、包围盒等元信息
   */
  async load(url: string, options?: LoadOptions): Promise<LoadResult> {
    this.ctx.registry.beginLoading(url)
    try {
      return await log.perf.span('load', async () => {
        const parsedUrl = decodeUrlSafe(url)
        const extension = getFileExtension(parsedUrl).toLowerCase()

        // 常规 JSON 是 LOD 元数据，meta.json 是目录 SOG 入口。
        if (extension === '.json' && getFileName(parsedUrl).toLowerCase() !== 'meta.json') {
          return this.loadLOD(url, options)
        }

        const supportedFormats = ['.sog', '.ply', '.splat']
        if (extension !== '.json' && !supportedFormats.includes(extension)) {
          throw new Error(
            `Unsupported file format: ${extension}. ` +
              `Supported formats: .json (LOD or directory SOG), ` +
              `${supportedFormats.join(', ')} (single file)`
          )
        }

        return this.loadSingleFile(url, options)
      })
    } finally {
      this.ctx.registry.endLoading(url)
    }
  }

  /** 销毁所有加载器 */
  dispose(): void {
    this.splatLoader = null
    this.sogBundleLoader = null
  }

  /** 更新加载器配置 */
  syncConfig(): void {
    // LOD 初始化使用按次创建的 SOGLoader；运行期资源加载器由 PCManager 同步配置。
  }

  // ==================== 私有：LOD 加载 ====================

  private async loadLOD(url: string, options?: LoadOptions): Promise<LoadResult> {
    throwIfAborted(options?.signal)

    return this.loadLODInternal(url, options)
  }

  private async loadLODInternal(url: string, options?: LoadOptions): Promise<LoadResult> {
    throwIfAborted(options?.signal)

    const endLodInit = log.perf.time('lodInit')
    let director: GSplatDirector
    let composition: LayerComposition
    try {
      ;({ director, composition } = measureGSplatLoadStageSync(
        options?.telemetry,
        {
          resourceType: 'lod-meta',
          resourceIdentity: url,
          resourceUrl: url,
          stage: 'lod-initialization',
        },
        () => {
          try {
            return this.ctx.initLODSystem()
          } finally {
            endLodInit()
          }
        }
      ))
    } catch (error) {
      annotateLodLoadError(error, 'lod-initialization')
      if (!isAbortError(error)) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        const errorName = error instanceof Error ? error.name : typeof error
        const errorStack = error instanceof Error ? (error.stack ?? '') : ''
        log.error(
          `[ModelLoader] Failed to load LOD: ${url}, error: ${errorMessage}, stage: lod-initialization, errorName: ${errorName}${errorStack !== '' ? `, stack: ${errorStack}` : ''}`
        )
      }
      throw error
    }
    let config = this.ctx.getLodConfig()
    const loadSHN = options?.loadSHN ?? config.loadSHN ?? true
    const clientId = generateNodeId()
    const finalNodeId = options?.nodeId ?? clientId

    // 如果已存在同 nodeId 的模型，先完整移除旧实例
    if (this.ctx.registry.nodeIdToClientId.has(finalNodeId)) {
      this.ctx.removeByNodeId(finalNodeId)
    }

    let octree: GSplatOctree | null = null
    let assetLoader: GSplatAssetLoader | null = null
    let rootNode: THREE.Object3D | null = null
    let layer: Layer | null = null
    let renderMesh: UnifiedLODMesh | null = null
    let centersPoints: UnifiedLODCentersPoints | null = null
    let entry: LODGroupEntry | null = null
    let layerRegistrationAttempted = false
    let sceneAttachmentAttempted = false
    let registryRegistrationAttempted = false
    let failureStage = 'create-sog-loader'

    try {
      failureStage = 'create-sog-loader'
      const sogLoader = new SOGLoader({
        debug: false,
        maxConcurrentLoads: config.maxConcurrentLoads,
        signal: options?.signal,
        withCredentials: this.ctx.withCredentials,
        telemetry: options?.telemetry,
      })

      // 预加载元数据
      failureStage = 'preload-lod-meta'
      const lodMeta = await sogLoader.preloadMeta(url)
      throwIfAborted(options?.signal)
      failureStage = 'parse-lod-meta'
      const offset = getSOGLodOffset(lodMeta)

      config = this.ctx.getLodConfig()

      // 解析 baseUrl
      failureStage = 'resolve-base-url'
      const parsedUrl = decodeUrlSafe(url)
      const shouldEncodeBaseUrl = parsedUrl !== url
      const baseUrl =
        options?.baseUrl ?? GSplatOctree.resolveBaseUrl(parsedUrl, { encode: shouldEncodeBaseUrl })

      // 创建 Octree
      failureStage = 'create-octree'
      octree = new GSplatOctree(
        url,
        {
          lodLevels: lodMeta.lodLevels,
          filenames: lodMeta.filenames,
          tree: lodMeta.tree,
          environment: typeof lodMeta.environment === 'string' ? lodMeta.environment : undefined,
        },
        {
          debug: false,
          loadEnvironment: options?.loadEnvironment,
          baseUrl,
          baseUrls: options?.baseUrls,
        }
      )

      // 创建 AssetLoader
      failureStage = 'create-asset-loader'
      config = this.ctx.getLodConfig()
      const getEventGroup = () => entry ?? { nodeId: finalNodeId, url }
      const createResourceNode = (resourceUrl: string) => ({
        nodeId: finalNodeId,
        url: resourceUrl,
      })
      const requestImmediateLodRefresh = () => {
        for (const manager of director.getAllManagers()) {
          manager.framesTillFullUpdate = 0
        }
      }
      assetLoader = new GSplatAssetLoader(this.ctx.renderer, {
        maxConcurrentLoads: config.maxConcurrentLoads,
        maxRetries: 2,
        debug: false,
        withCredentials: this.ctx.withCredentials,
        loadSHN,
        telemetry: options?.telemetry,
        onResourceLoaded: (resourceUrl) => {
          requestImmediateLodRefresh()
          this.ctx.emit('nodeLoaded', {
            node: createResourceNode(resourceUrl),
            group: getEventGroup(),
          })
        },
        onResourceGpuReady: (resourceUrl) => {
          requestImmediateLodRefresh()
          this.ctx.emit('nodeGpuReady', {
            node: createResourceNode(resourceUrl),
            group: getEventGroup(),
          })
        },
        onResourceFailed: (resourceUrl, error) => {
          this.ctx.emit('loadError', {
            node: createResourceNode(resourceUrl),
            group: getEventGroup(),
            error,
          })
        },
      })
      octree.assetLoader = assetLoader

      // 创建根节点
      failureStage = 'create-root-node'
      throwIfAborted(options?.signal)
      rootNode = new THREE.Object3D()
      rootNode.name = `LODGroup_${finalNodeId}`
      applyAssetRootTransform(rootNode, offset, options)

      // 计算包围盒和总点数
      failureStage = 'calculate-bounds-and-point-count'
      const boundingBox = sogLoader.getBoundingBox() ?? new THREE.Box3()
      let totalPointCount = 0
      for (const node of octree.nodes) {
        for (const lod of node.lods) {
          if (lod.count > 0) {
            totalPointCount += lod.count
            break
          }
        }
      }

      // 创建 Placement
      failureStage = 'create-placement'
      config = this.ctx.getLodConfig()
      const placement = new GSplatPlacement(null, rootNode)
      placement.octree = octree
      placement.aabb = boundingBox
      if (typeof config.lodBaseDistance === 'number') {
        placement.lodBaseDistance = config.lodBaseDistance
      }
      if (typeof config.lodMultiplier === 'number') {
        placement.lodMultiplier = config.lodMultiplier
      }
      placement.splatBudget = Math.max(1, Math.min(totalPointCount, config.maxCachePoints))

      // 创建专用 Layer
      failureStage = 'create-layer'
      layer = new Layer(`layer_${finalNodeId}`)
      layer.enabled = true
      layer.addGSplatPlacement(placement)
      layerRegistrationAttempted = true
      composition.addLayer(layer)

      // 将新 Layer 添加到所有已注册相机
      for (const cameraComponent of composition.cameras) {
        cameraComponent.addLayer(layer.id)
      }

      // 创建渲染 Mesh
      failureStage = 'create-render-mesh'
      const endMeshBuild = log.perf.time('meshBuild')
      try {
        renderMesh = new UnifiedLODMesh(director, composition, layer, boundingBox)
      } finally {
        endMeshBuild()
      }
      renderMesh.name = `LODRenderMesh_${finalNodeId}`
      rootNode.add(renderMesh)
      rootNode.updateMatrixWorld(true)
      const clipState = this.ctx.getClipState?.()
      if (clipState) {
        renderMesh.setClipState(clipState)
      }

      // 创建中心点渲染 Points
      failureStage = 'create-centers-points'
      centersPoints = new UnifiedLODCentersPoints(renderMesh)
      centersPoints.name = `LODCentersPoints_${finalNodeId}`
      rootNode.add(centersPoints)

      // 添加到场景
      failureStage = 'attach-scene-root'
      sceneAttachmentAttempted = true
      this.ctx.scene.add(rootNode)

      // 构建 Entry
      entry = {
        clientId,
        nodeId: finalNodeId,
        url,
        octree,
        placement,
        layer,
        rootNode,
        renderMesh,
        centersPoints,
        centersDisplayMode: 'gaussian-only' as CentersDisplayMode,
        centersStyle: {
          pointSize: DEFAULT_CENTERS_STYLE.pointSize,
          selectedColor: DEFAULT_CENTERS_STYLE.selectedColor.clone(),
          selectedOpacity: DEFAULT_CENTERS_STYLE.selectedOpacity,
          unselectedColor: DEFAULT_CENTERS_STYLE.unselectedColor.clone(),
          unselectedOpacity: DEFAULT_CENTERS_STYLE.unselectedOpacity,
        },
        boundingBox,
        totalPointCount,
        onLoadCompleteCallback: options?.onLoadComplete,
        loadCompleteCallbackFired: false,
        lastLoadingCount: undefined,
      }

      // 注册到 Registry
      failureStage = 'register-lod'
      registryRegistrationAttempted = true
      this.ctx.registry.registerLOD(entry)
      this.ctx.syncLodConfig?.()

      // 触发事件
      failureStage = 'emit-load-progress'
      this.ctx.emit('loadProgress', { loaded: 1, total: 1, group: entry })

      // 登记首次实例的稳定性追踪；实例出现前不影响其他资产的拾取路径。
      failureStage = 'track-lod-stability'
      this.ctx.trackLodStability(finalNodeId)

      return {
        type: 'lod',
        id: finalNodeId,
        boundingBox: convertBox3ToBoundingBox(boundingBox),
        numSplats: totalPointCount,
        environmentUrl: octree.environmentUrl,
        environmentConfigured: octree.environmentUrl !== null,
        _raw: entry,
      }
    } catch (error) {
      annotateLodLoadError(error, failureStage)
      if (registryRegistrationAttempted) {
        runRollbackCleanup('registry', () => this.ctx.registry.unregisterLOD(clientId))
      }
      if (sceneAttachmentAttempted && rootNode) {
        const ownedRootNode = rootNode
        runRollbackCleanup('scene-root', () => this.ctx.scene.remove(ownedRootNode))
      }
      if (centersPoints) {
        const ownedCentersPoints = centersPoints
        runRollbackCleanup('centers-points', () => ownedCentersPoints.dispose())
      }
      if (renderMesh) {
        const ownedRenderMesh = renderMesh
        runRollbackCleanup('render-mesh', () => ownedRenderMesh.dispose())
      }
      if (layerRegistrationAttempted && layer) {
        const ownedLayer = layer
        runRollbackCleanup('layer', () => composition.removeLayer(ownedLayer))
      }
      if (octree) {
        const ownedOctree = octree
        runRollbackCleanup('octree', () => ownedOctree.destroy())
      } else if (assetLoader) {
        const ownedAssetLoader = assetLoader
        runRollbackCleanup('asset-loader', () => ownedAssetLoader.destroy())
      }

      if (isAbortError(error)) {
        throw error
      }
      const errorMessage = error instanceof Error ? error.message : String(error)
      const errorName = error instanceof Error ? error.name : typeof error
      const errorStack = error instanceof Error ? (error.stack ?? '') : ''
      log.error(
        '[ModelLoader] Failed to load LOD: ' +
          url +
          ', error: ' +
          errorMessage +
          ', stage: ' +
          failureStage +
          ', errorName: ' +
          errorName +
          (errorStack !== '' ? `, stack: ${errorStack}` : '')
      )
      throw error
    }
  }

  // ==================== 私有：单文件加载 ====================

  private async loadSingleFile(url: string, options?: LoadOptions): Promise<LoadResult> {
    throwIfAborted(options?.signal)
    const extension = getFileExtension(url).toLowerCase()
    const clientId = generateNodeId()
    const finalNodeId = options?.nodeId ?? clientId
    let mesh: GaussianSplatMesh | null = null
    let sceneAttachmentAttempted = false
    let registryRegistrationAttempted = false

    try {
      if (extension === '.sog') {
        return this.loadSingleSogFile(url, options, clientId, finalNodeId)
      }
      if (extension === '.json') {
        return this.loadSingleSogDirectory(url, options, clientId, finalNodeId)
      }

      if (!this.splatLoader) {
        this.splatLoader = new SplatLoader()
      }
      const splatData = await this.splatLoader.load(url)

      // 创建 Mesh
      mesh = new GaussianSplatMesh(splatData, { enableSorting: true })
      mesh.name = `SingleFile_${finalNodeId}`
      applyTransform(mesh, options)

      // Registry 与 handle 保存 asset-local 包围盒；世界查询统一应用 mesh.matrixWorld。
      const boundingBox = getMeshLocalBoundingBox(mesh)

      // 自动添加到场景
      if (options?.autoAddToScene !== false) {
        sceneAttachmentAttempted = true
        this.ctx.scene.add(mesh)
      }

      // 构建 Entry
      const entry: SingleFileEntry = {
        clientId,
        nodeId: finalNodeId,
        mesh,
        url,
        numSplats: splatData.numSplats,
        boundingBox,
      }

      // 注册到 Registry
      registryRegistrationAttempted = true
      this.ctx.registry.registerSingle(entry)

      const result: LoadResult = {
        type: 'single' as const,
        id: finalNodeId,
        boundingBox: convertBox3ToBoundingBox(boundingBox),
        numSplats: splatData.numSplats,
        environmentUrl: undefined,
        environmentConfigured: false,
        _raw: mesh,
      }

      notifyLoadComplete(options?.onLoadComplete, result)

      return result
    } catch (error) {
      if (registryRegistrationAttempted) {
        runRollbackCleanup('registry', () => this.ctx.registry.unregisterSingle(clientId))
      }
      if (mesh) {
        const ownedMesh = mesh
        if (sceneAttachmentAttempted) {
          runRollbackCleanup('scene-mesh', () => this.ctx.scene.remove(ownedMesh))
        }
        runRollbackCleanup('mesh', () => ownedMesh.dispose())
      }
      log.error(
        '[ModelLoader] Failed to load single file: ' +
          url +
          ', error: ' +
          (error instanceof Error ? error.message : String(error))
      )
      throw error
    }
  }

  private async loadSingleSogFile(
    url: string,
    options: LoadOptions | undefined,
    clientId: string,
    finalNodeId: string
  ): Promise<LoadResult> {
    const signal = options?.signal
    throwIfAborted(signal)

    if (!this.sogBundleLoader) {
      this.sogBundleLoader = new SOGBundleLoader({
        keepCompressed: true,
        debug: false,
        withCredentials: this.ctx.withCredentials,
      })
    }

    const sogsData = await this.sogBundleLoader.loadGpuData(url, {
      renderer: this.ctx.renderer,
      preuploadTextures: true,
      signal,
      loadSHN: this.resolveLoadSHN(options),
      telemetry: options?.telemetry,
    })

    return this.createSingleSogModel(sogsData, url, options, clientId, finalNodeId)
  }

  private async loadSingleSogDirectory(
    url: string,
    options: LoadOptions | undefined,
    clientId: string,
    finalNodeId: string
  ): Promise<LoadResult> {
    const { textures, meta } = await SogsParser.load(url, {
      withCredentials: this.ctx.withCredentials,
      signal: options?.signal,
      loadSHN: this.resolveLoadSHN(options),
      telemetry: options?.telemetry,
    })

    let sogsData: GSplatSogsData
    try {
      sogsData = new GSplatSogsData({
        meta,
        numSplats: meta.count,
        means_l: textures.means_l,
        means_u: textures.means_u,
        quats: textures.quats,
        scales: textures.scales,
        sh0: textures.sh0,
        sh_centroids: textures.sh_centroids,
        sh_labels: textures.sh_labels,
        url,
      })
    } catch (error) {
      SogsParser.disposeTextures(textures)
      throw error
    }

    return this.createSingleSogModel(sogsData, url, options, clientId, finalNodeId)
  }

  private async createSingleSogModel(
    sogsData: GSplatSogsData,
    url: string,
    options: LoadOptions | undefined,
    clientId: string,
    finalNodeId: string
  ): Promise<LoadResult> {
    const signal = options?.signal
    let centers: Float32Array | undefined
    let mesh: GaussianSogsMesh | null = null
    let sceneAttachmentAttempted = false
    let registryRegistrationAttempted = false

    try {
      throwIfAborted(signal)
      try {
        await measureGSplatLoadStage(
          options?.telemetry,
          {
            resourceType: url.toLowerCase().endsWith('.sog') ? 'sog-bundle' : 'sog-meta',
            resourceIdentity: url,
            resourceUrl: url,
            stage: 'center-generation',
          },
          () => sogsData.generateCenters(this.ctx.renderer, signal)
        )
        throwIfAborted(signal)
        centers = sogsData.getCenters() ?? undefined
      } catch (error) {
        if (isAbortError(error)) {
          throw error
        }
        throwIfAborted(signal)
        const message = error instanceof Error ? error.message : 'Unknown error'
        log.warn(`[ModelLoader] SOG centers generation failed, sorting disabled: ${message}`)
      }

      const endMeshBuild = log.perf.time('meshBuild')
      try {
        mesh = measureGSplatLoadStageSync(
          options?.telemetry,
          {
            resourceType: url.toLowerCase().endsWith('.sog') ? 'sog-bundle' : 'sog-meta',
            resourceIdentity: url,
            resourceUrl: url,
            stage: 'resource-create',
          },
          () =>
            new GaussianSogsMesh(sogsData, {
              enableSorting: centers !== undefined,
              centers,
            })
        )
      } finally {
        endMeshBuild()
      }
      throwIfAborted(signal)
      mesh.name = `SingleFile_${finalNodeId}`
      applyTransform(mesh, options)

      const boundingBox = getMeshLocalBoundingBox(mesh)

      if (options?.autoAddToScene !== false) {
        throwIfAborted(signal)
        sceneAttachmentAttempted = true
        this.ctx.scene.add(mesh)
      }
      throwIfAborted(signal)

      const entry: SingleFileEntry = {
        clientId,
        nodeId: finalNodeId,
        mesh,
        url,
        numSplats: sogsData.numSplats,
        boundingBox,
      }

      registryRegistrationAttempted = true
      this.ctx.registry.registerSingle(entry)
      throwIfAborted(signal)

      const result: LoadResult = {
        type: 'single' as const,
        id: finalNodeId,
        boundingBox: convertBox3ToBoundingBox(boundingBox),
        numSplats: sogsData.numSplats,
        environmentUrl: undefined,
        environmentConfigured: false,
        _raw: mesh,
      }

      notifyLoadComplete(options?.onLoadComplete, result)
      throwIfAborted(signal)

      return result
    } catch (error) {
      if (registryRegistrationAttempted) {
        runRollbackCleanup('registry', () => this.ctx.registry.unregisterSingle(clientId))
      }
      if (mesh) {
        const ownedMesh = mesh
        if (sceneAttachmentAttempted) {
          runRollbackCleanup('scene-mesh', () => this.ctx.scene.remove(ownedMesh))
        }
        runRollbackCleanup('mesh', () => ownedMesh.dispose())
      } else {
        runRollbackCleanup('sogs-data', () => sogsData.destroy())
      }
      throw error
    }
  }

  private resolveLoadSHN(options?: LoadOptions): boolean {
    return options?.loadSHN ?? this.ctx.getLodConfig().loadSHN ?? true
  }
}

// ==================== 工具函数（从 PCManager 下沉） ====================

/** 生成唯一的内部 ID */
function generateNodeId(): string {
  return `gs3d_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw new DOMException('Aborted', 'AbortError')
  }
}

function isAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError'
  )
}

/** 获取文件扩展名 */
function getFileExtension(url: string): string {
  const fileName = getFileName(url)
  const lastDot = fileName.lastIndexOf('.')
  return lastDot !== -1 ? fileName.substring(lastDot).toLowerCase() : ''
}

/** 获取忽略 query/hash 的最终文件名。 */
function getFileName(url: string): string {
  let pathToExtract = decodeUrlSafe(url)
  const queryPathMatch = pathToExtract.match(/^([^?]+\?path=)(.*)$/)
  if (queryPathMatch) {
    pathToExtract = queryPathMatch[2]
  } else {
    const localFileMatch = pathToExtract.match(/^(https?:\/\/[^/]+\/local-file\/)(.+)$/)
    if (localFileMatch) {
      pathToExtract = localFileMatch[2]
    }
  }

  pathToExtract = pathToExtract.split('?')[0].split('#')[0]

  try {
    pathToExtract = decodeURIComponent(pathToExtract)
  } catch {
    // 解码失败，使用原始路径
  }
  const lastSlash = Math.max(pathToExtract.lastIndexOf('/'), pathToExtract.lastIndexOf('\\'))
  return lastSlash !== -1 ? pathToExtract.substring(lastSlash + 1) : pathToExtract
}

/** 安全解码 URL */
function decodeUrlSafe(url: string): string {
  try {
    return decodeURIComponent(url)
  } catch {
    return url
  }
}

/**
 * 将 THREE.Box3 转换为统一的 BoundingBox 格式。
 *
 * @param box - 源 Box3
 * @returns 含 min/max 纯对象的 BoundingBox
 */
export function convertBox3ToBoundingBox(box: THREE.Box3): BoundingBox {
  return {
    min: { x: box.min.x, y: box.min.y, z: box.min.z },
    max: { x: box.max.x, y: box.max.y, z: box.max.z },
  }
}

/** 读取单文件 Mesh 的 asset-local 包围盒并隔离内部引用。 */
function getMeshLocalBoundingBox(
  mesh: THREE.Object3D & { geometry?: THREE.BufferGeometry }
): THREE.Box3 {
  return mesh.geometry?.boundingBox?.clone() ?? new THREE.Box3()
}

/** 运行回滚步骤，避免清理异常覆盖原始加载错误。 */
function runRollbackCleanup(label: string, cleanup: () => void): void {
  try {
    cleanup()
  } catch (cleanupError) {
    log.warn(
      `[ModelLoader] rollback failed at ${label}: ${
        cleanupError instanceof Error ? cleanupError.message : String(cleanupError)
      }`
    )
  }
}

/** 隔离加载完成观察者，资产提交成功后回调异常不改变加载结果。 */
function notifyLoadComplete(
  callback: LoadOptions['onLoadComplete'] | undefined,
  result: LoadResult
): void {
  if (!callback) return
  try {
    callback(result)
  } catch (error) {
    log.warn(
      `[ModelLoader] onLoadComplete callback failed: ${
        error instanceof Error ? error.message : String(error)
      }`
    )
  }
}

/** 应用变换 */
function applyTransform(obj: THREE.Object3D, options?: LoadOptions): void {
  if (options?.position) {
    obj.position.set(options.position.x, options.position.y, options.position.z)
  }
  if (options?.rotation) {
    obj.rotation.set(options.rotation.x, options.rotation.y, options.rotation.z)
  }
  if (options?.scale) {
    obj.scale.set(options.scale.x, options.scale.y, options.scale.z)
  }
}
